import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Users, 
  Trophy, 
  Gift, 
  Copy, 
  Share2, 
  Ticket,
  ChevronRight,
  CheckCircle
} from 'lucide-react';

export default function ReferralsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralInfo, setReferralInfo] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [info, rankingData, rewardsData] = await Promise.all([
        api.getMyReferralInfo(),
        api.getReferralRanking(),
        api.getMyRewards()
      ]);
      setReferralInfo(info);
      setRanking(rankingData);
      setRewards(rewardsData.rewards || []);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (referralInfo?.referral_code) {
      navigator.clipboard.writeText(referralInfo.referral_code);
      toast.success('¡Código copiado!');
    }
  };

  const copyLink = () => {
    if (referralInfo?.referral_link) {
      navigator.clipboard.writeText(referralInfo.referral_link);
      toast.success('¡Link copiado!');
    }
  };

  const shareLink = async () => {
    if (navigator.share && referralInfo?.referral_link) {
      try {
        await navigator.share({
          title: 'TuPrima - La que alegra el día',
          text: '¡Únete a TuPrima y participa por $1.000.000! Usa mi código de referido:',
          url: referralInfo.referral_link
        });
      } catch (err) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  const claimReward = async (rewardId) => {
    setClaiming(true);
    try {
      const result = await api.claimReward(rewardId);
      toast.success(`¡Ticket gratis reclamado! Número: ${result.ticket_numbers.join(', ')}`);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al reclamar';
      toast.error(message);
    } finally {
      setClaiming(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container pt-6 space-y-6">
      <h1 className="heading-2">Invita y Gana</h1>

      {/* How it works */}
      <Card className="card-base bg-gradient-to-br from-cyan-50 to-blue-50" data-testid="referral-info">
        <CardContent className="p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" />
            ¿Cómo funciona?
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center flex-shrink-0">1</span>
              <p>Comparte tu código o link con amigos</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center flex-shrink-0">2</span>
              <p>Cuando tu amigo <strong>compre tickets</strong>, se convierte en <span className="text-green-600 font-medium">Referido Activo</span></p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center flex-shrink-0">3</span>
              <p>Por cada <strong>20 referidos activos</strong>, ganas <strong>1 ticket GRATIS</strong></p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center flex-shrink-0">🏆</span>
              <p>El que más <strong>referidos activos</strong> tenga en el mes gana <strong className="text-primary">{formatCurrency(1000000)}</strong></p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Code */}
      <Card className="card-base" data-testid="my-referral-code">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Tu Código de Referido</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-800 rounded-xl">
            <span className="font-mono text-xl font-bold text-cyan-400">
              {referralInfo?.referral_code}
            </span>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={copyCode}
              className="text-white hover:bg-slate-700"
              data-testid="copy-code-btn"
            >
              <Copy className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={copyLink}
              variant="outline"
              className="flex-1 rounded-full"
              data-testid="copy-link-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar Link
            </Button>
            <Button 
              onClick={shareLink}
              className="flex-1 btn-primary"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Compartir
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="card-base border-2 border-green-200 bg-green-50" data-testid="stat-active">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <p className="text-3xl font-bold text-green-700">{referralInfo?.active_referrals || 0}</p>
            <p className="text-xs text-green-600 font-medium">Referidos Activos</p>
            <p className="text-xs text-green-500">Ya compraron tickets</p>
          </CardContent>
        </Card>
        <Card className="card-base border-2 border-amber-200 bg-amber-50" data-testid="stat-invited">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-amber-500" />
            <p className="text-3xl font-bold text-amber-700">{referralInfo?.invited_referrals || 0}</p>
            <p className="text-xs text-amber-600 font-medium">Referidos Invitados</p>
            <p className="text-xs text-amber-500">Pendientes de compra</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Stats for Prize */}
      <Card className="card-base bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200" data-testid="stat-monthly">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-700 font-medium">Referidos Activos del Mes</p>
              <p className="text-xs text-purple-500">Solo estos cuentan para el premio mensual</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-purple-700">{referralInfo?.monthly_referrals || 0}</p>
              <Trophy className="w-5 h-5 text-purple-500 inline" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Stats */}
      <Card className="card-base" data-testid="stat-total">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-slate-500" />
              <span className="text-sm text-muted-foreground">Total de Referidos</span>
            </div>
            <span className="text-xl font-bold">{referralInfo?.total_referrals || 0}</span>
          </div>
        </CardContent>
      </Card>

      {/* Progress to next ticket */}
      <Card className="card-base" data-testid="progress-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Progreso al próximo ticket gratis</span>
            <span className="font-bold text-green-600">{referralInfo?.pending_tickets || 0} / 20</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all"
              style={{ width: `${((referralInfo?.pending_tickets || 0) / 20) * 100}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Te faltan <strong>{20 - (referralInfo?.pending_tickets || 0)} referidos activos</strong> para tu próximo ticket gratis
          </p>
        </CardContent>
      </Card>

      {/* Pending Rewards */}
      {rewards.length > 0 && (
        <Card className="card-base border-primary/30" data-testid="rewards-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              Recompensas Disponibles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {rewards.map((reward) => (
              <div 
                key={reward.id}
                className="flex items-center justify-between p-3 bg-primary/5 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <Ticket className="w-6 h-6 text-primary" />
                  <div>
                    <p className="font-medium">{reward.tickets} Ticket{reward.tickets > 1 ? 's' : ''} Gratis</p>
                    <p className="text-xs text-muted-foreground">Por referidos</p>
                  </div>
                </div>
                <Button
                  onClick={() => claimReward(reward.id)}
                  disabled={claiming}
                  size="sm"
                  className="rounded-full"
                  data-testid={`claim-reward-${reward.id}`}
                >
                  Reclamar
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tickets Earned */}
      {(referralInfo?.free_tickets_earned || 0) > 0 && (
        <Card className="card-base bg-success/5" data-testid="earned-card">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-success" />
            <div>
              <p className="font-medium">Has ganado {referralInfo?.free_tickets_earned} tickets gratis</p>
              <p className="text-sm text-muted-foreground">¡Sigue invitando amigos!</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Prize */}
      <Card className="card-base bg-gradient-to-r from-amber-50 to-orange-50" data-testid="monthly-prize">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <Trophy className="w-8 h-8 text-amber-500" />
            <div>
              <h3 className="font-semibold">Premio Mensual</h3>
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(1000000)}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            El usuario con más referidos al final del mes gana este premio. 
            ¡Cada mes se reinicia el contador!
          </p>
        </CardContent>
      </Card>

      {/* Ranking */}
      {ranking.length > 0 && (
        <Card className="card-base" data-testid="ranking-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Top Referidores del Mes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {ranking.slice(0, 5).map((entry, index) => (
              <div 
                key={entry.user_id}
                className={`flex items-center justify-between p-3 rounded-xl ${
                  entry.user_id === user?.id ? 'bg-primary/10 ring-1 ring-primary' :
                  index === 0 ? 'bg-amber-50' : 'bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0 ? 'bg-amber-400 text-amber-900' :
                    index === 1 ? 'bg-slate-300 text-slate-700' :
                    index === 2 ? 'bg-amber-700 text-amber-100' :
                    'bg-slate-200 text-slate-600'
                  }`}>
                    {entry.position}
                  </div>
                  <span className="font-medium">
                    {entry.user_name}
                    {entry.user_id === user?.id && <span className="text-primary ml-1">(Tú)</span>}
                  </span>
                </div>
                <span className="font-mono text-sm">{entry.monthly_referrals} ref.</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
